/**
 * API surface for the ported report components.
 *
 * Mirrors the handful of helpers ViewResult and TelemetryChart imported from
 * the recruiter app's api/client, but routed through this app's axios instance
 * so requests carry the Super Admin's bearer token.
 */
import api from '../../../services/api';

/**
 * Render the report's own HTML to a PDF server-side.
 *
 * Same endpoint the recruiter app uses; it only requires an authenticated
 * user, so a Super Admin token is accepted as-is.
 */
export const generatePDFFromHTML = async (
  htmlContent,
  sessionId,
  filename,
  compactMode = false,
) => {
  const response = await api.post(
    '/generate-pdf/',
    {
      html_content: htmlContent,
      session_id: sessionId,
      filename,
      compact_mode: compactMode,
    },
    { responseType: 'blob' },
  );
  return response.data;
};

/**
 * Telemetry timeseries for the report's connection-health chart.
 *
 * Uses the Super Admin endpoint rather than /sessions/<id>/telemetry/, which
 * is company-scoped and therefore unreachable for another company's interview.
 */
export const fetchSessionTelemetry = async (sessionId) => {
  const response = await api.get(
    `/super-admin/interview-sessions/${sessionId}/telemetry/`,
  );
  return response.data;
};

/**
 * Resolve the playable URL for a session's full recording.
 *
 * The session payload's own session_video_url points at the recruiter app's
 * /secure-recording/<id> page, which does not exist in this panel, so the
 * report asks this endpoint directly instead. It authorises super users and
 * returns the storage URL (a local media path or an Azure SAS URL).
 */
export const fetchSessionRecordingUrl = async (sessionId) => {
  const data = await fetchSecureRecording(sessionId);
  return data?.video_url || null;
};

/**
 * Full secure-recording payload (video URL, session meta, question sidebar).
 * Powers the same recording player company admins use.
 */
export const fetchSecureRecording = async (sessionId) => {
  const response = await api.get(`/secure-recording/${sessionId}/`);
  return response.data;
};

export default api;
